/**
 * @file Windows95.ts
 * @description The main state file for the desktop interface of The Diagnosis Game
 */

// engine imports
import { GAME_DEBUG_MODE } from "../../engine/Constants/Constants";
import { BootAssets } from "../../config/BootAssets";
import { tsthreeConfig } from "../../config/tsthreeConfig";
import { Engine, GameObject, HelperFunctions, State } from "../../tsthree";
import { Spritesheet, Texture } from "pixi.js";

// cores
import { CaseUpdateType, MailCore } from "../Logic/MailCore";
import { PlayerDataSingleton } from "../Logic/PlayerDataSingleton";
import { NarrativeSingleton } from "../Logic/NarrativeSingleton";

// windows/prefabs
import { Windows95Window } from "../Prefabs/Windows95Window";
import { Win95MailWindow } from "../Prefabs/Windows/Win95MailWindow";
import { Win95CaseSelectionWindow } from "../Prefabs/Windows/Win95CaseSelectionWindow";
import { Win95DoctorStats } from "../Prefabs/Windows/Win95DoctorStats";
import { Win95MailPopoutWindow } from "../Prefabs/Windows/Win95MailPopoutWindow";
import { Win95SettingsWindow } from "../Prefabs/Windows/Win95SettingsWindow";
import { Win95InternetExplorerWindow } from "../Prefabs/Windows/Win95InternetExplorerWindow";
import { Windows95Taskbar } from "../Prefabs/Windows95Taskbar";
import { Windows95Desktop } from "../Prefabs/Windows95Desktop";
import { DoctOSWindowType, Win95DoctOSWindow } from "../Prefabs/Windows/Win95DoctOSWindow";
import { LoginScreen } from "./LoginScreen";
// import { Win95MessagingMainWindow } from "../Prefabs/Windows/Win95MessagingMainWindow";

// Types/enums
import { IWindowState } from "../Types/IWindowState";
import { MailType } from "../Types/MailType";
import { IMedCaseJSON } from "../Types/IMedCaseJSON";
import { ProcessState } from "../Constants/ProcessState";
import { WindowID } from "../Types/WindowID";
import { AUDIO_TYPES } from "../../engine/AudioSingleton";
import { AnalyticsEventID } from "../Constants/AnalyticsEventIDs";
import { IInitialWindowStates } from "../Types/IInitialWindowStates";
import { IContactStatus } from "../Types/IContactStatus";
import { LoaderType } from "../../engine/Loaders/LoaderType";

// Helper functions
import { uid } from "../../engine/HelperFunctions/uid";
import { bringToFront } from "../../engine/HelperFunctions/bringToFront";
import { uppercaseFirstLettersInString } from "../../engine/HelperFunctions/uppercaseFirstLettersInString";
import { closeFullscreen, getIsFullscreen, openFullscreen } from "../../engine/HelperFunctions/fullscreenFunctions";
import { ShowBluescreen } from "../Logic/ShowBluescreen";

// data
import { PLACEHOLDER_MAIL_DATA } from "../Data/PlaceholderMailData";
import { CoreFailReason, HospitalNetCore } from "../Logic/HospitalNetCore";
import { SEARCH_HOMEPAGE } from "../Data/WebsiteContents";
import { MailState } from "../Types/MailState";
import { getCaseAmountsFromXP } from "../Data/SelectACaseUnlocks";
import { DoctorRanks } from "../Constants/DoctorRanks";
import { SAVE_KEYS } from "../Constants/SaveKeys";
import { GAME_FONT, GAME_FONT_BOLD } from "../Constants/Constants";
import { HospitalIDMapper } from "../Data/HospitalIDMapper";
import { AchievementID, Achievements } from "../Data/Achievements";

// components
import { FTUTutorialComponent } from "../Components/FTUTutorialComponent";
import { TutorialStageComponent } from "../Components/TutorialStageComponent";
import { Win95MessagingWindow } from "../Prefabs/Windows/Win95MessagingWindow";

// Global runtime props
const CHECK_BUFFER: number = 6000;
let __HANDLING_LINK: boolean = false;
let __TASKBAR_SPAM_PREVENT = false;

export class Windows95 extends State {

    private _checkingCases: boolean = false;

    private _windowStates: IWindowState<Windows95Window>[] = [];

    private _taskbar: Windows95Taskbar;

    private _musicId: string = "";

    private _initialWindowStates: IInitialWindowStates | null = null;

    private _tutorialGO: GameObject;

    private _lastTimestampUpdate: number = 0;

    constructor(_initialWindowStates?: IInitialWindowStates) {
        super();
        if(_initialWindowStates) {
            this._initialWindowStates = _initialWindowStates;
        }
    }

    public refreshMailUI(): void {
        (this._windowStates
            .find((e) => e.id == "MailWindow")
            .windowObj as Win95MailWindow)
            .forceUpdate();
    }

    public extractInitialWindowState(): IInitialWindowStates {
        const retVal: IInitialWindowStates = {};

        this._windowStates.forEach((e) => {
            retVal[e.id] = {
                selected: e.selected,
                closed: e.closed,
            };
        });

        return retVal;
    }

    onStep(_engine: Engine) {
        super.onStep(_engine);

        let handledTaskbarUpdate: boolean = false;
        for(let i = 0; i < this._windowStates.length; i++) {
            if(this._windowStates[i].dirty) {
                handledTaskbarUpdate = true;
                if(this._windowStates[i].selected) {
                    this._windowStates.forEach((e) => e.selected = false);
                    this._windowStates[i].selected = true;
                }
                this._taskbar.setWindowsState(this._windowStates);
                this._windowStates[i].dirty = false;
            }
            if(handledTaskbarUpdate) {
                this._windowStates[i].dirty = false;
            }
        }

        if(!this._checkingCases) {
            this._checkingCases = true;
            if(PlayerDataSingleton.hasAnyActiveExamsTreatments()) {
                if(this._lastTimestampUpdate !== 0) {
                    PlayerDataSingleton.addToDayTimer(
                        Date.now() - this._lastTimestampUpdate
                    );
                }
                this._lastTimestampUpdate = Date.now();
            } else {
                this._lastTimestampUpdate = 0;
            }
            const promises = [];
            for (const activeCase of PlayerDataSingleton.getCases()) {
                for(let i = 0; i < activeCase.attemptedExaminations.length; i++) {
                    const e = activeCase.attemptedExaminations[i];
                    if(e.state == ProcessState.EXAM_IN_PROGRESS) {
                        if(e.endTime + (CHECK_BUFFER) <= PlayerDataSingleton.getGameTime()) {
                            promises.push(
                                HospitalNetCore.acknowledgeExam(
                                    activeCase.id,
                                    e.id,
                                ).then((res) => {
                                    if(res.error) {
                                        return;
                                    }
                                    PlayerDataSingleton.setCase(res.json);

                                    const mailId = `UPDATE-${
                                        activeCase.patientName
                                    }-${CaseUpdateType.Exam}-${
                                        e.id
                                    }-true`;

                                    MailCore.sendEmailToPlayer(
                                        mailId,
                                        MailType.CASE_UPDATE,
                                    );
                                    PlayerDataSingleton.setCase(
                                        Object.assign(res.json)
                                    );

                                    const data = MailCore.createMaildataFromCase(res.json);
                                    MailCore.addCustomMail(`CASE_${res.id}`, data);
                                    // console.log(MailCore.debug_getMailDataFromId(`CASE_${res.id}`));
                                })
                            );
                        }
                    }
                }

                for(let i = 0; i < activeCase.currentTreatments.length; i++) {
                    const e = activeCase.currentTreatments[i];
                    if(e.state == ProcessState.EXAM_IN_PROGRESS && e.endTime + (CHECK_BUFFER) <= PlayerDataSingleton.getGameTime()) {
                        promises.push(
                            HospitalNetCore.acknowledgeTreatment(
                                activeCase.id,
                                e.id,
                            ).then((res) => {
                                if(res.error) {
                                    return;
                                }
                                PlayerDataSingleton.setCase(res.json);
                                const mailId = `UPDATE-${
                                    activeCase.patientName
                                }-${CaseUpdateType.Treatment}-${
                                    e.id
                                }-true`;

                                MailCore.sendEmailToPlayer(
                                    mailId,
                                    MailType.CASE_UPDATE,
                                );
                                PlayerDataSingleton.setCase(
                                    Object.assign(res.json)
                                );

                                const data = MailCore.createMaildataFromCase(res.json);
                                MailCore.addCustomMail(`CASE_${res.id}`, data);
                            })
                        );
                    }
                }
            }
            Promise.allSettled(promises).then((e) => setTimeout(() => this._checkingCases = false, 2000));
        }
    }

    // public async updateContacts(): Promise<void> {
    //     const state: IWindowState<Win95MessagingMainWindow>
    //         = this._windowStates.find(
    //             (e) => e.id === WindowID.MessagingContactsWindow
    //         ) as IWindowState<Win95MessagingMainWindow>;
    //
    //     state.windowObj.updateStatus(
    //         NarrativeSingleton.getContactState()
    //     );
    // }

    public playMusic(): Promise<void> {
        return HelperFunctions.playSound_s("Warmth", {
            loop: true,
            volume: 0.33,
            audioType: AUDIO_TYPES.MUSIC
        })
            .then((s) => {
                this._musicId = s;
            });
    }

    private getWindowStatesFromID(_wid: WindowID): IWindowState<Windows95Window>[] {
        return this._windowStates.filter((e) => e.id === _wid);
    }

    private async handleOpenLinkFromEmail(
        url: string, caseId: string
    ): Promise<void> {
        if(
            __HANDLING_LINK ||
            (this._tutorialGO?.getComponent(FTUTutorialComponent)?.arbitraryStageId === "BASIC_EXAM_TUTORIAL" ||
                this._tutorialGO?.getComponent(FTUTutorialComponent)?.arbitraryStageId === "SCHEDULE_EXAM_TUTORIAL")
        ) {
            return;
        } else {
            __HANDLING_LINK = true;
        }

        let isRequest = false;
        const ind = PlayerDataSingleton.getCases().findIndex((e) => e.id === caseId);
        // const _case = this._activeCases[ind];
        const patientNames = PlayerDataSingleton.getCases().map((e) => e.patientName);

        // HACK
        if(
            TutorialStageComponent.instances
                .find(
                    (e) => e.tutorialStageId === "CaseEmailStage"
                )?.isActive
            && (
                url === "requestexam" ||
                url === "requesttreatment" ||
                url === "makediagnosis" ||
                url === "abandoncase"
            )
        ) {
            __HANDLING_LINK = false;
            return;
        }
        // HACK

        if(
            url == "requestexam"
        ) {
            this.getWindowStatesFromID(WindowID.DoctOSWindow)
                .forEach((e) => {
                    (e.windowObj as Win95DoctOSWindow).update(
                        ind,
                        patientNames,
                        DoctOSWindowType.Exam
                    );
                });
            isRequest = true;
        } else if(url == "requesttreatment") {
            this.getWindowStatesFromID(WindowID.DoctOSWindow)
                .forEach((e) => {
                    (e.windowObj as Win95DoctOSWindow).update(
                        ind,
                        patientNames,
                        DoctOSWindowType.Treatment
                    );
                });
            isRequest = true;
        } else if(url == "makediagnosis") {
            this.getWindowStatesFromID(WindowID.DoctOSWindow)
                .forEach((e) => {
                    (e.windowObj as Win95DoctOSWindow).update(
                        ind,
                        patientNames,
                        DoctOSWindowType.Diagnosis
                    );
                });
            isRequest = true;
        } else if(url == "abandoncase") {
            if(GAME_DEBUG_MODE)
                console.log("abandoning case %s", caseId);

            PlayerDataSingleton.removeCase(caseId);
            MailCore.deleteMaildata(
                `CASE_${caseId}`
            );
            MailCore.deleteMailInstance(`CASE_${caseId}`);
            this.getWindowStatesFromID(WindowID.EmailInbox)
                .forEach((e) => {
                    (e.windowObj as Win95MailWindow).forceUpdate();
                });
        } else {
            this.getWindowStatesFromID(WindowID.InternetTraveller)
                .forEach((e) => {
                    (e.windowObj as Win95InternetExplorerWindow).changePage(url);
                    e.selected = true;
                    e.closed = false;
                    e.windowObj.visible = true;
                    bringToFront(e.windowObj);
                });
            this.getWindowStatesFromID(WindowID.MailPopoutWindow)
                .forEach((e) => {
                    bringToFront(e.windowObj);
                });
        }

        if(isRequest) {
            this.getWindowStatesFromID(WindowID.DoctOSWindow)
                .forEach((e) => {
                    const doctoswindowstate = e;
                    this._windowStates.forEach((e) => e.selected = false);
                    doctoswindowstate.selected = true;
                    doctoswindowstate.closed = false;
                    bringToFront(doctoswindowstate.windowObj);
                    doctoswindowstate.windowObj.visible = true;
                    doctoswindowstate.windowObj.hasContext = true;
                });

            setTimeout(() => {
                this.getWindowStatesFromID(WindowID.DoctOSWindow)
                    .forEach((e) => {
                        (e.windowObj as Win95DoctOSWindow).searchBar.setAcceptInput();
                    });
            }, 10);
        }

        this.getWindowStatesFromID(WindowID.EmailInbox)
            .forEach((e) => {
                e.selected = false;
                this._taskbar.setWindowsState(this._windowStates);
                (e.windowObj as Win95MailWindow)["selectedMailContents"]
                    .container.getComponent(TutorialStageComponent)?.onActivate?.();

            });
        __HANDLING_LINK = false;
    }

    private onMinimise(_uid: string): void {
        if(GAME_DEBUG_MODE) console.log("Minimising %s", _uid);
        // windowsStates.find((e) => e.id == _uid).closed = true;
        const state = this._windowStates.find((e) => e.uid == _uid);
        state.selected = false;
        state.windowObj.visible = false;
        this._taskbar.setWindowsState(this._windowStates);
    }

    private onClose(_uid: string): void {
        if(GAME_DEBUG_MODE) console.log("Closing %s", _uid);
        const stateIndex = this._windowStates.findIndex((e) => e.uid == _uid);
        const state = this._windowStates[stateIndex];
        state.closed = true;
        state.selected = false;
        state.windowObj.visible = false;
        // windowsStates.find((e) => e.id == _uid).selected = false;
        this._taskbar.setWindowsState(this._windowStates);
    }
    
    private onStartMenuItemSelected(i: number): void {
        if(__TASKBAR_SPAM_PREVENT) return;
        __TASKBAR_SPAM_PREVENT = true;
        switch(i) {
            case 0:
                // new case
                this._windowStates.forEach((e) => {
                    e.selected = false;
                });
                this.getWindowStatesFromID(WindowID.SelectACaseWindow)
                    .forEach((e: IWindowState<Win95CaseSelectionWindow>) => {
                        e.closed = false;
                        e.selected = true;
                        e.windowObj.visible = true;
                        bringToFront(e.windowObj);
                        this._taskbar.setWindowsState(this._windowStates);
                        if(!e.windowObj["_cases"]) {
                            e.windowObj.getComponent(TutorialStageComponent)?.onActivate?.();
                            __TASKBAR_SPAM_PREVENT = false;
                            this.handleCaseSelection(
                                this._windowStates,
                                e.uid,
                                this._taskbar
                            )
                                .catch((err) => {
                                    console.error(err);
                                    ShowBluescreen(err.stack);
                                    __TASKBAR_SPAM_PREVENT = false;
                                })
                                .then((e) => {
                                    __TASKBAR_SPAM_PREVENT = false;
                                    if(e) {
                                        Windows95.updateMailDataForCases(PlayerDataSingleton.getCases());
                                        PlayerDataSingleton.getCases().forEach((e) => {
                                            MailCore.sendEmailToPlayer(
                                                `CASE_${e.id}`,
                                                MailType.CASE
                                            );
                                        });
                                        this.getWindowStatesFromID(WindowID.EmailInbox)
                                            .forEach((w: IWindowState<Win95MailWindow>) => {
                                                w.windowObj.forceUpdate();
                                            });
                                    }
                                    this.getWindowStatesFromID(WindowID.SelectACaseWindow)
                                        .forEach((w: IWindowState<Win95CaseSelectionWindow>) => {
                                            w.windowObj["_cases"] = null;
                                        });
                                });
                        } else {
                            this.getWindowStatesFromID(WindowID.SelectACaseWindow)
                                .forEach((w: IWindowState<Win95CaseSelectionWindow>) => {
                                    try {
                                        (w.windowObj).update(
                                            undefined,
                                            PlayerDataSingleton.getDocScore(),
                                            undefined,
                                        );
                                    } catch(err) {
                                        ShowBluescreen(err.stack);
                                    }
                                });
                            __TASKBAR_SPAM_PREVENT = false;
                        }
                        this._taskbar.getComponent(TutorialStageComponent)?.onActivate?.();
                    });
                break;
            case 1:
                // doctor stats
                __TASKBAR_SPAM_PREVENT = false;
                this._windowStates.forEach((e) => e.selected = false);
                const dstatsState =
                    this._windowStates.find((e) => e.id == WindowID.DoctorStatsWindow);
                dstatsState.closed = false;
                dstatsState.selected = true;
                dstatsState.windowObj.visible = true;
                bringToFront(dstatsState.windowObj);
                this._taskbar.setWindowsState(this._windowStates);
                break;
            case 2:
                // internet explorer
                __TASKBAR_SPAM_PREVENT = false;
                this._windowStates.find((e) => {
                    e.selected = false;
                });
                this.getWindowStatesFromID(WindowID.InternetTraveller)
                    .forEach((e: IWindowState<Win95InternetExplorerWindow>) => {
                        e.closed = false;
                        e.selected = true;
                        e.windowObj.visible = true;
                        bringToFront(e.windowObj);
                    });
                this._taskbar.setWindowsState(this._windowStates);
                break;
            case 3:
                // mail server
                __TASKBAR_SPAM_PREVENT = false;
                this._windowStates.find((e) => {
                    e.selected = false;
                });
                this.getWindowStatesFromID(WindowID.EmailInbox)
                    .forEach((e: IWindowState<Win95MailWindow>) => {
                        e.closed = false;
                        e.selected = true;
                        e.windowObj.visible = true;
                        bringToFront(e.windowObj);
                    });
                this._taskbar.setWindowsState(this._windowStates);
                break;
            case 4:
                // settings
                __TASKBAR_SPAM_PREVENT = false;
                this._windowStates.forEach((e) => e.selected = false);
                this.getWindowStatesFromID(WindowID.SettingsWindow)
                    .forEach((e: IWindowState<Win95MailWindow>) => {
                        e.closed = false;
                        e.selected = true;
                        e.windowObj.visible = true;
                        bringToFront(e.windowObj);
                    });
                this._taskbar.setWindowsState(this._windowStates);
                break;
            case 5:
                // go fullscreen
                if(!getIsFullscreen()) {
                    openFullscreen().catch().finally(() => {
                        __TASKBAR_SPAM_PREVENT = false;
                    });
                    return;
                } else {
                    closeFullscreen();
                    setTimeout(() => {
                        if(!__TASKBAR_SPAM_PREVENT) return;
                        (function refreshState() {
                            ENGINE.resizeRenderer(
                                window.innerWidth * 0.80,
                                window.innerHeight * 0.80,
                            );
                            ENGINE.changeState(new Windows95(
                                (ENGINE.getActiveState() as Windows95)
                                    .extractInitialWindowState()
                            ));
                        })();
                        __TASKBAR_SPAM_PREVENT = false;
                    }, 17);
                }
                break;
            case 6:
                // Help
                this.handleOpenLinkFromEmail(
                    "http://help.doctos.org",
                    null,
                ).then(() => __TASKBAR_SPAM_PREVENT = false);
                break;
            case 7:
                // logout
                PlayerDataSingleton.setAuthKey("", "offline");
                ENGINE.changeState(
                    new LoginScreen()
                );
                break;
        }
    }

    private createMailWindow(): IWindowState<Win95MailWindow> {


        const windowMailUID = uid();
        const testWindowState: IWindowState<Win95MailWindow> = {
            id: WindowID.EmailInbox,
            uid: windowMailUID,
            windowObj: null,
            title: null,
            icon: null,
            selected: false,
            closed: true,
        };
        const mailWindow = new Win95MailWindow({
            windowStateRef: testWindowState,
            onClose: () => this.onClose(windowMailUID),
            onMinimise: () => this.onMinimise(windowMailUID),
            mailInstances: PlayerDataSingleton.getMailboxContents(),
            openLink: (...args) => this.handleOpenLinkFromEmail(...args),
            onOpenPopoutWindow: (_contentsStr, _subject) => {
                this._windowStates.forEach((e) => e.selected = false);
                this.getWindowStatesFromID(WindowID.MailPopoutWindow)
                    .forEach((e: IWindowState<Win95MailPopoutWindow>) => {
                        bringToFront(e.windowObj);
                        e.closed = false;
                        e.selected = true;
                        e.windowObj.visible = true;
                        // state.windowObj.windowName = _subject;
                        e.windowObj.update(
                            _contentsStr,
                            _subject,
                            this.handleOpenLinkFromEmail
                        );
                    });
                this._taskbar.setWindowsState(this._windowStates);
            },

            // unused?
            onUpdateMailState: () => {

            },
        });
        testWindowState.windowObj = mailWindow;
        testWindowState.title = mailWindow.windowName;
        testWindowState.icon = mailWindow.icon;
        mailWindow.visible = false;

        return testWindowState;
    }

    private createITravellerWindow(): IWindowState<Win95InternetExplorerWindow> {
        const iexplorerUID = uid();
        const iexplorerState: IWindowState<Win95InternetExplorerWindow> = {
            id: WindowID.InternetTraveller,
            uid: iexplorerUID,
            windowObj: null,
            title: null,
            icon: null,
            selected: false,
            closed: true,
        };
        const iexplorerWindow = new Win95InternetExplorerWindow({
            windowStateRef: iexplorerState,
            onClose: () => this.onClose(iexplorerUID),
            onMinimise: () => this.onMinimise(iexplorerUID),
        });
        iexplorerState.icon = iexplorerWindow.icon;
        iexplorerState.title = iexplorerWindow.windowName;
        iexplorerState.windowObj = iexplorerWindow;
        iexplorerWindow.changePage(SEARCH_HOMEPAGE);
        iexplorerWindow.visible = false;
        return iexplorerState;
    }

    private createSettingsWindow(): IWindowState<Win95SettingsWindow> {
        const settingsUID = uid();
        const settingsState: IWindowState<Win95SettingsWindow> = {
            id: WindowID.SettingsWindow,
            uid: settingsUID,
            windowObj: null,
            title: null,
            icon: null,
            selected: false,
            closed: true,
        };
        const settingsWindow = new Win95SettingsWindow({
            windowStateRef: settingsState,
            onClose: () => this.onClose(settingsUID),
            onMinimise: () => this.onMinimise(settingsUID),
        });
        settingsWindow.position.set(
            tsthreeConfig.width * 0.3,
            tsthreeConfig.height * 0.25,
        );
        settingsState.windowObj = settingsWindow;
        settingsState.title = settingsWindow.windowName;
        settingsState.icon = settingsWindow.icon;
        settingsWindow.visible = false;
        return settingsState;
    }

    // private createDSNWindow(
    //     _onOpenChatWindow: (_contactData: IContactStatus) => void,
    // ): IWindowState<Win95MessagingMainWindow> {
    //     const dsnUID = uid();
    //     const dsnState: IWindowState<Win95MessagingMainWindow> = {
    //         id: WindowID.MessagingContactsWindow,
    //         uid: dsnUID,
    //         windowObj: null,
    //         title: null,
    //         icon: null,
    //         selected: false,
    //         closed: true,
    //     };
    //     const dsnWindow = new Win95MessagingMainWindow({
    //         windowStateRef: dsnState,
    //         onClose: () => this.onClose(dsnUID),
    //         onMinimise: () => this.onMinimise(dsnUID),
    //         onOpenChatWindow: _onOpenChatWindow,
    //     });
    //     dsnWindow.updateStatus(NarrativeSingleton.getContactState());
    //
    //     // fixme: if the pos is set higher than this, the header bg is too wide. I have no idea why.
    //     dsnWindow.position.set(
    //         tsthreeConfig.width * 0.32,
    //         tsthreeConfig.height * 0.15,
    //     );
    //     dsnState.windowObj = dsnWindow;
    //     dsnState.title = dsnWindow.windowName;
    //     dsnState.icon = dsnWindow.icon;
    //     dsnWindow.visible = false;
    //     return dsnState;
    // }

    private createDSNChatWindow(): IWindowState<Win95MessagingWindow> {
        const dsnChatUID = uid();
        const dsnState: IWindowState<Win95MessagingWindow> = {
            id: WindowID.MessagingWindow,
            uid: dsnChatUID,
            windowObj: null,
            title: null,
            icon: null,
            selected: false,
            closed: true,
        };
        const dsnWindow = new Win95MessagingWindow({
            windowStateRef: dsnState,
            onClose: () => this.onClose(dsnChatUID),
            onMinimise: () => this.onMinimise(dsnChatUID),
        });

        dsnWindow.position.set(
            tsthreeConfig.width * 0.32,
            tsthreeConfig.height * 0.15,
        );
        dsnState.windowObj = dsnWindow;
        dsnState.title = dsnWindow.windowName;
        dsnState.icon = dsnWindow.icon;
        dsnWindow.visible = false;
        return dsnState;
    }

    private createSelectACaseWindow(): IWindowState<Win95CaseSelectionWindow> {
        const selectACaseUID = uid();
        const selectACaseState: IWindowState<Win95CaseSelectionWindow> = {
            id: WindowID.SelectACaseWindow,
            uid: selectACaseUID,
            windowObj: null,
            title: null,
            icon: null,
            selected: false,
            closed: true,
        };
        const selectACaseWindow = new Win95CaseSelectionWindow({
            windowStateRef: selectACaseState,
            onClose: () => this.onClose(selectACaseUID),
            onMinimise: () => this.onMinimise(selectACaseUID)
        });
        selectACaseWindow.addComponent(new TutorialStageComponent({
            id: "SelectACaseStage"
        }));
        selectACaseState.windowObj = selectACaseWindow;
        selectACaseState.title = selectACaseWindow.windowName;
        selectACaseState.icon = selectACaseWindow.icon;
        selectACaseWindow.visible = false;
        return selectACaseState;
    }

    private createMailPopoutWindow(_zoomFactor?: number): IWindowState<Win95MailPopoutWindow> {
        const mailPopoutUID = uid();
        const mailPopoutState: IWindowState<Win95MailPopoutWindow> = {
            id: WindowID.MailPopoutWindow,
            uid: mailPopoutUID,
            windowObj: null,
            title: null,
            icon: null,
            selected: false,
            closed: true,
        };
        const mailPopoutWindow = new Win95MailPopoutWindow({
            onClose: () => this.onClose(mailPopoutUID),
            windowStateRef: mailPopoutState,
            onMinimise: () => this.onMinimise(mailPopoutUID),
            textStyle: {
                "default": {
                    fontFamily: GAME_FONT,
                    fill: 0x000000,
                    fontSize: 11 * (_zoomFactor || 1),
                    align: "left",
                    wordWrap: true,
                    wordWrapWidth: (tsthreeConfig.width - 32)
                },
                "red": {
                    fill: "#ff0000",
                },
                "green": {
                    fontFamily: GAME_FONT_BOLD,
                    // fontWeight: "bold",
                    fill: "#00aa00",
                },
                "bold": {
                    fontFamily: GAME_FONT_BOLD,
                    // fontWeight: "bold"
                },
                "i": {
                    fontStyle: "italic",
                },
                "hyperlink": {
                    fill: "#0000ff",
                    textDecoration: "underline"
                },
            },
            openLink: (...params) => this.handleOpenLinkFromEmail(...params),
        });
        mailPopoutState.windowObj = mailPopoutWindow;
        mailPopoutState.title = mailPopoutWindow.windowName;
        mailPopoutState.icon = mailPopoutWindow.icon;
        mailPopoutWindow.visible = false;
        return mailPopoutState;
    }

    private createDoctorStatsWindow(): IWindowState<Win95DoctorStats> {
        const doctorStatsUID = uid();
        const doctorStatsWindowState: IWindowState<Win95DoctorStats> = {
            id: WindowID.DoctorStatsWindow,
            uid: doctorStatsUID,
            windowObj: null,
            title: null,
            icon: null,
            selected: false,
            closed: true,
        };
        const doctorStatsWindow = new Win95DoctorStats({
            onClose: () => this.onClose(doctorStatsUID),
            onMinimise: () => this.onMinimise(doctorStatsUID),
            windowStateRef: doctorStatsWindowState
        });
        doctorStatsWindowState.windowObj = doctorStatsWindow;
        doctorStatsWindowState.title = doctorStatsWindow.windowName;
        doctorStatsWindowState.icon = doctorStatsWindow.icon;
        doctorStatsWindow.update();
        doctorStatsWindow.position.set(
            Math.floor((tsthreeConfig.width / 2) - (doctorStatsWindow.width  / 2)),
            Math.floor((tsthreeConfig.height / 2)- (doctorStatsWindow.height / 2))
        );
        doctorStatsWindow.visible = false;

        return doctorStatsWindowState;
    }

    onAwake(_engine: Engine, _params?: unknown): void {
        ENGINE.getSaveHandler().allowedToSave = true;

        _engine.setBackgroundColor(0x008080);
        this.scene.getStage().sortableChildren = true;

        const windowsStates: IWindowState<Windows95Window>[] = this._windowStates;

        // _engine.setBackgroundColor(0x008080, 0);

        function desktopOnClick(_uid: string, _taskbar: Windows95Taskbar): void {
            windowsStates.forEach((e) => e.selected = false);
            const state = windowsStates.find((e) => e.uid == _uid);
            state.closed = false;
            state.selected = true;
            state.windowObj.visible = true;
            bringToFront(state.windowObj);
            _taskbar.setWindowsState(windowsStates);
        }
        const tutorialStageMail: TutorialStageComponent | null = new TutorialStageComponent({
            id: "MailDesktopIconTutorial"
        });
        const desktop = new Windows95Desktop([
            {
                name: "Mail",
                icon: ENGINE.getPIXIResource("unread-icon") as Texture,
                onClick: () => {
                    desktopOnClick(testWindowState.uid, this._taskbar);
                    tutorialStageMail?.onActivate?.();
                },
                components: [tutorialStageMail]
            },
            {
                name: "Internet Traveller",
                icon: ENGINE.getPIXIResource("iexplorer-page-icon") as Texture,
                onClick: () => desktopOnClick(iexplorerState.uid, this._taskbar)
            },
            {
                name: "Doctor\nStats",
                icon: ENGINE.getPIXIResource("document-icon-24px") as Texture,
                onClick: () => desktopOnClick(doctorStatsWindowState.uid, this._taskbar)
            },
            {
                name: "Settings",
                icon: ENGINE.getPIXIResource("settings-icon-24px") as Texture,
                onClick: () => desktopOnClick(settingsState.uid, this._taskbar)
            }
        ]);
        this.scene.addObject(desktop);

        MailCore.initialize(PLACEHOLDER_MAIL_DATA);

        // initialize with cases here:

        // MailCore.initialize();

        Windows95.updateMailDataForCases(PlayerDataSingleton.getCases());
        PlayerDataSingleton.getCases().forEach((e, i) => {
            MailCore.sendEmailToPlayer(
                `CASE_${e.id}`,
                MailType.CASE,
                MailState.READ,
                e.startTime
            );
        });

        // end

        const testWindowState = this.createMailWindow();
        this.scene.addObject(testWindowState.windowObj);
        windowsStates.push(testWindowState);

        const iexplorerState = this.createITravellerWindow();
        windowsStates.push(iexplorerState);
        this.scene.addObject(iexplorerState.windowObj);

        const settingsState = this.createSettingsWindow();
        windowsStates.push(settingsState);
        this.scene.addObject(settingsState.windowObj);

        // const dsnState = this.createDSNWindow(
        //     (_contactData: IContactStatus) => {
        //         if(dsnChatState.closed) {
        //             dsnChatState.windowObj.position.set(
        //                 Math.min(
        //                     tsthreeConfig.width - 80,
        //                     dsnState.windowObj.x + 64),
        //                 Math.min(
        //                     tsthreeConfig.height - 80,
        //                     dsnState.windowObj.y + 64),
        //             );
        //         }
        //         desktopOnClick(dsnChatState.uid, this._taskbar);
        //         const convo = NarrativeSingleton.getFirstValidConversationForContact(
        //             _contactData.id
        //         );
        //         if(convo) {
        //             dsnChatState.windowObj.update(
        //                 _contactData,
        //                 convo,
        //                 PlayerDataSingleton.hasSeenConversation(convo.scriptId)
        //             );
        //         } else {
        //             console.error(
        //                 "Failed to find valid conversation for contact %s!",
        //                 _contactData.id
        //             );
        //         }
        //     }
        // );
        // dsnState.windowObj.updateStatus(
        //
        // )
        // windowsStates.push(dsnState);
        // this.scene.addObject(dsnState.windowObj);

        const dsnChatState = this.createDSNChatWindow();
        windowsStates.push(dsnChatState);
        this.scene.addObject(dsnChatState.windowObj);

        const doctOSWindow = this.createDoctOSWindow();
        windowsStates.push(doctOSWindow);
        this.scene.addObject(doctOSWindow.windowObj);

        const selectACaseState = this.createSelectACaseWindow();
        windowsStates.push(selectACaseState);
        this.scene.addObject(selectACaseState.windowObj);

        const mailPopoutState = this.createMailPopoutWindow(
            testWindowState.windowObj["zoomFactor"]
        );
        windowsStates.push(mailPopoutState);
        this.scene.addObject(mailPopoutState.windowObj);

        const taskbarTutorialComponent = new TutorialStageComponent({
            id: "TaskbarTutorialStage"
        });
        this._taskbar = new Windows95Taskbar({
            onStartMenuItemSelected: (i) => this.onStartMenuItemSelected(i),
            onStartButtonClicked: () => {
                console.log("Start button clicked");
            },
            onWindowSelected: (_state) => {
                // _state.windowObj.visible = true;
                windowsStates.forEach((e) => {
                    if(e.uid !== _state.uid) {
                        // e.windowObj.visible = false;
                    }
                });
                _state.windowObj.visible = true;
                _state.windowObj.headerBgGrey.visible = false;
                _state.windowObj.headerBg.visible = true;
                bringToFront(_state.windowObj);
            },
            startMenuEntries: [
                {
                    id: "RequestCase",
                    text: "Request new case",
                    icon: ENGINE.getTexture("newcase-icon-24px"),
                },
                {
                    id: "DoctorStats",
                    text: "Doctor Stats",
                    icon: ENGINE.getTexture("document-icon-24px"),
                },
                {
                    id: "Internet",
                    text: "Internet Traveller",
                    icon: ENGINE.getTexture("iexplorer-page-icon"),
                },
                {
                    id: "Mail",
                    text: "Mail Server",
                    icon: ENGINE.getTexture("read-icon"),
                },
                {
                    id: "Settings",
                    text: "Settings",
                    icon: ENGINE.getTexture("settings-icon-24px"),
                },
                {
                    id: "GoFullScreen",
                    text: getIsFullscreen() ? "Go Windowed" : "Go Fullscreen",
                    icon: ENGINE.getTexture("popout-icon"),
                },
                {
                    id: "Help",
                    text: "Help Page",
                    icon: ENGINE.getTexture("help-icon-24px"),
                },
                {
                    id: "Logout",
                    text: "Log out",
                    icon: ENGINE.getTexture("shutdown-icon"),
                },
            ]
        });
        this._taskbar.addComponent(taskbarTutorialComponent);
        this._taskbar.zIndex = Number.MAX_SAFE_INTEGER - 10;
        this._taskbar.position.set(
            0, tsthreeConfig.height
        );

        const doctorStatsWindowState = this.createDoctorStatsWindow();
        windowsStates.push(doctorStatsWindowState);
        this.scene.addObject(doctorStatsWindowState.windowObj);

        if(this._initialWindowStates) {
            if(GAME_DEBUG_MODE)
                console.log("setting initial window state %o", this._initialWindowStates);
            Object.keys(this._initialWindowStates).forEach((e) => {
                const found = windowsStates.find((w) => w.id === e);
                if(found) {
                    found.selected = this._initialWindowStates[e].selected;
                    found.closed = this._initialWindowStates[e].closed;
                    found.windowObj.visible = !found.closed;
                }
            });
            this._initialWindowStates = null;
        }

        this._taskbar.setWindowsState(windowsStates);
        this.scene.addObject(this._taskbar);

        if(!PlayerDataSingleton.hasSeenFTUTutorial()) {
            const tutorialGO = this._tutorialGO = new GameObject();
            tutorialGO.addComponent(
                new FTUTutorialComponent()
            );
            this.scene.addObject(tutorialGO);
            tutorialGO.getComponent(FTUTutorialComponent).start();
        } else if(
            !PlayerDataSingleton.getIsMusicMuted()
        ) {
            this.playMusic();
        }

        // taskbar.selectWindow(0);
        _engine.getTicker().start();
    }

    private static updateMailDataForCases(_cases: IMedCaseJSON[]): void {
        _cases.forEach((e) => {
            const data = MailCore.createMaildataFromCase(e);
            MailCore.addCustomMail(`CASE_${e.id}`, data);
        });
    }

    async preload(_engine: Engine): Promise<void> {
        const assets: typeof BootAssets = [];
        if(!_engine.hasPIXIResource("AchievementIconsSpritesheet")) {
            assets.push(
                {
                    key: "AchievementIconsSpritesheet",
                    path: "sprites/Spritesheets/AchievementIcons.json",
                    type: LoaderType.PIXI,
                },
            );
        }
        if(!_engine.hasPIXIResource("CharacterMakerSpritesheet")) {
            assets.push({
                key: "CharacterMakerSpritesheet",
                path: "sprites/Spritesheets/charactermaker.json",
                type: LoaderType.PIXI,
            });
        }
        if(ENGINE["_loadingScrObject"]) {
            ENGINE["_loadingScrObject"].visible = true;
        }
        let promise: Promise<void>;
        if(assets.length > 0) {
            promise = _engine.loadAssets(assets, (p) => {
                if(GAME_DEBUG_MODE) {
                    console.log("Windows95 preload progress %i", p);
                }
                if(ENGINE["_loadingScrObject"]) {
                    // @ts-ignore
                    tsthreeConfig.loadingScreenComponent["progress"] = p;
                }
            });
        } else {
            promise = Promise.resolve(undefined);
        }

        await promise;

        if(_engine.hasPIXIResource("AchievementIconsSpritesheet")) {
            const spritesheet = _engine.getPIXIResource("AchievementIconsSpritesheet") as Spritesheet;
            const success = _engine.processSpritesheet(
                spritesheet
            );

            // check achvs here
            if(GAME_DEBUG_MODE) {
                const keys = Object.keys(Achievements);
                keys.forEach((e) => {
                    const keyAsAchvId: AchievementID = parseInt(e);
                    if(
                        !Achievements[keyAsAchvId].name
                    ) return;
                    const sprKey = `achievement-${keyAsAchvId}`;
                    if(!spritesheet.data.frames[sprKey]) {
                        console.warn("[TDG-32] Missing achievement icon %s, for achievement %s", sprKey, Achievements[
                            keyAsAchvId
                            ].name);
                    }
                });
            }

            if(success) {
                console.log("Successfully parsed AchievementIconsSpritesheet");
            }
        }
        if(ENGINE["_loadingScrObject"]) {
            ENGINE["_loadingScrObject"].visible = false;
        }
        // const promises: Promise<any>[] = [];
        //
        // promises.push(
        //     Windows95.fetchAndUpdateCases()
        //         .then((e) => {
        //             this._activeCases = e;
        //         })
        // );
        //
        // return Promise.allSettled(promises).then(() => console.log("Finished preloading Windows95"));
    }

    onDestroy(_engine: Engine) {
        super.onDestroy(_engine);
        ENGINE.getStage().children.forEach((e) => {
            e.destroy({
                children: true,
                texture: false,
                baseTexture: false
            });
        });
        ENGINE.getStage().removeChildren();
        HelperFunctions.stopAllSoundsOfId("Warmth");
    }

    private handleCaseSelection<T extends Windows95Window>(
        windowsStates: Array<IWindowState<T>>,
        selectACaseUID: string,
        taskbar: Windows95Taskbar,
    ): Promise<boolean> {

        const _cases: Array<Partial<IMedCaseJSON>> = HospitalNetCore.generateCases(
            getCaseAmountsFromXP(PlayerDataSingleton.getDocScore())
        );

        const e = _cases;
        if(GAME_DEBUG_MODE)
            console.log("Generated cases: %o", e);
        return new Promise<boolean>((res, rej) => {
            const state = windowsStates.find((e) => e.uid === selectACaseUID);
            (state.windowObj as unknown as Win95CaseSelectionWindow).update(
                e as IMedCaseJSON[],
                PlayerDataSingleton.getDocScore(),
                (_selectedCaseJSON: IMedCaseJSON) => {
                    if(!_selectedCaseJSON) return res(false);
                    (state.windowObj as unknown as Win95CaseSelectionWindow)["_caseElements"][0]
                        .container.getComponent(TutorialStageComponent)?.onActivate?.();
                    if(_selectedCaseJSON && _selectedCaseJSON.id) {
                        if(GAME_DEBUG_MODE)
                            console.log("Added case %s", _selectedCaseJSON.id);
                        windowsStates.forEach((w) => {
                            if(w.uid == selectACaseUID) {
                                w.closed = true;
                                w.windowObj.visible = false;
                                w.selected = false;
                            }
                        });
                        PlayerDataSingleton.addCase(_selectedCaseJSON);
                        taskbar.setWindowsState(windowsStates);
                        res(true);
                    } else {
                        return res(false);
                    }
                }
            );
        });
    }

    private async onRequest(
        _type: DoctOSWindowType,
        _id: string,
        _patientName: string,
    ): Promise<void> {
        const _case = PlayerDataSingleton.getCases().find(
            (e) => e.patientName.toLowerCase() === _patientName.toLowerCase()
        );
        if(!_case) {
            throw new Error("Failed to find case for patient " + _patientName);
            return;
        }

        if(GAME_DEBUG_MODE)
            console.log("Requesting a %s on case ID %s", _id, _case.id);

        let requestResult: {id: string, json: IMedCaseJSON, reason?: CoreFailReason} | null = null;
        switch(_type) {
            case DoctOSWindowType.Exam:
                requestResult = await HospitalNetCore.requestExam(_case.id, _id);
                if(requestResult != null) {
                    if(requestResult.id && requestResult.json) {
                        PlayerDataSingleton.setCase(requestResult.json);

                        ENGINE.logEvent(
                            AnalyticsEventID.RequestExam,
                            1,
                            {
                                examId: _id
                            }
                        );

                        const gastroId = "oMethhaIK3o!taFdntxGj3HBlqA=";
                        if(
                            _id === (gastroId) &&
                            PlayerDataSingleton
                                .getCases()
                                .filter(
                                    (e) => e.attemptedExaminations.findIndex(
                                        (e) => e.id === gastroId
                                    ) !== -1
                                )
                                .length >= 3
                        ) {
                            PlayerDataSingleton.addAchievement(AchievementID.DOCTOR_BUTT);
                        }

                        const mailId = MailCore.buildUpdateIdString(
                            _patientName,
                            CaseUpdateType.Exam,
                            _id,
                            requestResult.json.attemptedExaminations[
                            requestResult.json.attemptedExaminations.length-1
                                ].endTime
                        );
                        MailCore.addCustomMail(
                            mailId,
                            MailCore.createMaildataFromCaseUpdate(
                                MailCore.sendEmailToPlayer(
                                    mailId,
                                    MailType.CASE_UPDATE,
                                    undefined,
                                    undefined,
                                    false,
                                )
                            )
                        );
                        MailCore.addCustomMail(
                            `CASE_${_case.id}`,
                            MailCore.createMaildataFromCase(
                                requestResult.json
                            )
                        );
                    } else if(
                        requestResult.reason
                    ) {
                        switch(requestResult.reason) {
                            case CoreFailReason.PATIENT_CANT_TALK:
                                this._taskbar.showNotification(
                                    {
                                        id: "PATIENT_CANT_TALK_" + uid(),
                                        mainStr: `Your requested examination (${
                                            HospitalIDMapper.idToStringObject[_id]
                                        }) could not be requested as the patient is unable to speak!`,
                                        titleStr: "Exam failed!",
                                        iconKey: "warning-icon"
                                    }
                                );
                                break;
                            default:
                                break;
                        }
                    }
                } else {
                    throw new Error("No response from requestExam");
                }
                break;
            case DoctOSWindowType.Treatment:
                const treatmentResult = await HospitalNetCore.giveTreatment(
                    _case.id,
                    _id
                );
                if(treatmentResult != null) {
                    PlayerDataSingleton.setCase(treatmentResult.json);
                    ENGINE.logEvent(
                        AnalyticsEventID.RequestTreatment,
                        1,
                        {
                            examId: _id
                        }
                    );
                    const mailId = MailCore.buildUpdateIdString(
                        _patientName,
                        CaseUpdateType.Treatment,
                        _id,
                        treatmentResult.json.currentTreatments[
                        treatmentResult.json.currentTreatments.length-1
                            ].endTime
                    );//;
                    MailCore.addCustomMail(
                        mailId,
                        MailCore.createMaildataFromCaseUpdate(
                            MailCore.sendEmailToPlayer(
                                mailId,
                                MailType.CASE_UPDATE,
                                undefined,
                                undefined,
                                false,
                            )
                        )
                    );
                    MailCore.addCustomMail(
                        `CASE_${_case.id}`,
                        MailCore.createMaildataFromCase(
                            treatmentResult.json
                        )
                    );
                }
                break;
            case DoctOSWindowType.Diagnosis:
                const diagnosisResult = await HospitalNetCore.makeDiagnosis(
                    _case,
                    _id
                );
                if(diagnosisResult.error) {
                    // safe fail
                } else {
                    PlayerDataSingleton.addPastCase(_case);
                    if(diagnosisResult.success) {
                        if(_case.difficultyScore >= 6) {
                            PlayerDataSingleton.addAchievement(AchievementID.SEEING_STARS);
                        }
                        if((PlayerDataSingleton.getGameTime() - _case.startTime) <= (1000 * 60)) {
                            PlayerDataSingleton.addAchievement(AchievementID.QUICK_ON_THE_DRAW);
                        }

                        PlayerDataSingleton.incrementNumOfSolvedCases();
                        PlayerDataSingleton.addDocScore(
                            diagnosisResult.score
                        );

                        // success!
                        MailCore.deleteMaildata(
                            `CASE_${_case.id}`
                        );
                        MailCore.deleteMailInstance(`CASE_${_case.id}`);
                        MailCore.deleteUpdatesFromCase(_case);
                        const mailId = MailCore.buildDiagnosisIdString(
                            _case.id,
                            _patientName,
                            _id,
                            true,
                            diagnosisResult,
                        );//;
                        MailCore.addCustomMail(
                            mailId,
                            MailCore.createMaildataFromDiagnosis(
                                MailCore.sendEmailToPlayer(
                                    mailId,
                                    MailType.CASE_UPDATE,
                                    undefined,
                                    undefined,
                                    true,
                                ),
                                _case
                            )
                        );

                        ENGINE.logEvent(
                            AnalyticsEventID.CorrectDiagnosis,
                            1,
                            {
                                diagnosisId: _case.diagnosisId
                            }
                        );

                        // Windows95.fetchAndUpdateCases().then((e) => {
                        //     this._activeCases = e;
                        //     if(this._activeCases.length)
                        //     Windows95.updateMailDataForCases(this._activeCases);
                        //     this._activeCases.forEach((e) => {
                        //         MailCore.sendEmailToPlayer(
                        //             `CASE_${e.id}`,
                        //             MailType.CASE
                        //         );
                        //     });
                        //     mailWindow.forceUpdate();
                        // });

                        this.getWindowStatesFromID(
                            WindowID.EmailInbox
                        ).forEach((e: IWindowState<Win95MailWindow>) => {
                            e.windowObj.forceUpdate();
                        });
                    } else {
                        // hard fail
                        MailCore.deleteMailInstance(`CASE_${_case.id}`);
                        MailCore.deleteUpdatesFromCase(_case);
                        MailCore.deleteMaildata(`CASE_${_case.id}`);

                        if(_case.difficultyScore <= 1.49) {
                            PlayerDataSingleton.addAchievement(AchievementID.ONE_STAR_FAILURE);
                        }

                        ENGINE.logEvent(
                            AnalyticsEventID.IncorrectDiagnosis,
                            1,
                            {
                                diagnosisId: _case.diagnosisId
                            }
                        );

                        PlayerDataSingleton.incrementNumOfIncorrectDiagnoses();
                        PlayerDataSingleton.addDocScore(
                            diagnosisResult.score
                        );

                        const mailId = MailCore.buildDiagnosisIdString(
                            _case.id,
                            _patientName,
                            _id,
                            false,
                            {
                                docScore: diagnosisResult.score,
                                breakdown: diagnosisResult.breakdown
                            },
                        );//;
                        MailCore.addCustomMail(
                            mailId,
                            MailCore.createMaildataFromDiagnosis(
                                MailCore.sendEmailToPlayer(
                                    mailId,
                                    MailType.CASE_UPDATE,
                                    undefined,
                                    undefined,
                                    true,
                                ),
                                _case
                            )
                        );
                        PlayerDataSingleton.dirtify(SAVE_KEYS.MailboxContents);
                        this.getWindowStatesFromID(
                            WindowID.EmailInbox
                        ).forEach((e: IWindowState<Win95MailWindow>) => {
                            e.windowObj.forceUpdate();
                        });
                    }
                    const newXP = (PlayerDataSingleton.getDocScore() + diagnosisResult.score);
                    let currRank = -1, newRank = -1;
                    for(let i = 0; i < DoctorRanks.length; i++) {
                        if(PlayerDataSingleton.getDocScore() >= DoctorRanks[i].threshold) {
                            currRank = i;
                        }
                        if(newXP >= DoctorRanks[i].threshold) {
                            newRank = i;
                        }
                    }

                    (ENGINE.getActiveState() as Windows95)["_taskbar"]?.showNotification({
                        id: "XP_EARNED_" + uid(),
                        iconKey: "doctorstats-icon",
                        titleStr: `You gained ${diagnosisResult.score} XP!`,
                        mainStr: `Make sure to check your Doctor Stats!`
                    });

                    if(currRank !== newRank) {
                        // send new rank email
                        MailCore.sendEmailToPlayer(
                            "RANK_UP_EMAIL_" + newRank,
                            MailType.RANK_UP_EMAIL
                        );
                    }

                    this.getWindowStatesFromID(
                        WindowID.DoctorStatsWindow
                    ).forEach((e: IWindowState<Win95DoctorStats>) => {
                        e.windowObj.update();
                    });
                    PlayerDataSingleton.removeCase(_case.id);
                }
                break;
        }
    }

    private createDoctOSWindow(): IWindowState<Win95DoctOSWindow> {
        const doctOSUID = uid();
        const doctOSState: IWindowState<Win95DoctOSWindow> = {
            id: WindowID.DoctOSWindow,
            uid: doctOSUID,
            windowObj: null,
            title: null,
            icon: null,
            selected: false,
            closed: true,
        };
        const doctOSWindow = new Win95DoctOSWindow({
            onRequest: (...params) => this.onRequest(...params),
            windowStateRef: doctOSState,
            onClose: () => this.onClose(doctOSUID),
            onMinimise: () => this.onMinimise(doctOSUID),
        });
        doctOSState.windowObj = doctOSWindow;
        doctOSState.title = doctOSWindow.windowName;
        doctOSState.icon = doctOSWindow.icon;
        doctOSWindow.update(
            0,
            PlayerDataSingleton.getCases()
                .map((e) => uppercaseFirstLettersInString(e.patientName)),
            DoctOSWindowType.Exam,
        );
        doctOSWindow.visible = false;
        return doctOSState;
    }
}